import type { App } from 'vue';
import { watch } from 'vue';
import type { Router } from 'vue-router';
import { userStore } from '@/stores/authStore';
import { projectStore } from '@/stores/project';
import { roleStore } from '@/stores/role';
import { profileStore } from '@/stores/profile';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: any[]) => void;
    webVitals?: any;
  }
}

interface AnalyticsOptions {
  router: Router;
  measurementId?: string;
}

const loadGtag = (measurementId: string): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && window.gtag) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => {
      const dl = (window.dataLayer = window.dataLayer || []);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        dl.push(arguments);
      };
      window.gtag('js', new Date());
      // We will send manual page_view on route changes
      window.gtag('config', measurementId, { send_page_view: false });
      resolve();
    };
    document.head.appendChild(script);
  });
};

const AnalyticsPlugin = {
  install: async (app: App, options: AnalyticsOptions) => {
    const measurementId = options?.measurementId;
    const router = options?.router;

    if (!measurementId) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[Analytics] VUE_APP_GA_MEASUREMENT_ID is not set. Skipping GA init.');
      }
      return;
    }

    await loadGtag(measurementId);

    // Consent Mode v2 defaults: analytics allowed, ads denied by default
    try {
      window.gtag?.('consent', 'default', {
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        ad_storage: 'denied',
        analytics_storage: 'granted',
        functionality_storage: 'granted',
        personalization_storage: 'granted',
        security_storage: 'granted',
      });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[GA] consent default error', e);
    }

    const trackPageView = (path: string, title?: string) => {
      if (!window.gtag) return;
      window.gtag('config', measurementId, {
        page_path: path,
        page_title: title,
        anonymize_ip: true,
      });
    };

    const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
      if (!window.gtag) return;
      window.gtag('event', eventName, params || {});
    };

    // User/project context helpers
    const setUserContext = () => {
      try {
        const auth = userStore();
        const profile = profileStore();
        const project = projectStore();
        const role = roleStore();
        const userId =
          (profile as any)?.profile?.memberInProjectIdx ||
          (profile as any)?.profile?.memberIdx ||
          '';
        if (userId) {
          window.gtag?.('set', { user_id: String(userId) });
        }
        window.gtag?.('set', 'user_properties', {
          role: role.isClient ? 'client' : 'professional',
          projectId: project.selectedProjectId ?? '',
          projectName: project.selectedProject?.name ?? '',
          isLoggedIn: auth.login ? 'true' : 'false',
        });
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.debug('[GA] setUserContext error', e);
      }
    };

    const setConsent = (updates: Record<string, 'granted' | 'denied'>) => {
      try {
        window.gtag?.('consent', 'update', updates);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.debug('[GA] consent update error', e);
      }
    };

    app.provide('$ga', { trackEvent, trackPageView, setUserContext, setConsent });

    if (router) {
      // Dwell time tracking
      let lastPath = '';
      let lastStart = performance.now();
      router.afterEach((to, from) => {
        const now = performance.now();
        if (from && from.fullPath) {
          const dwell = Math.max(0, now - lastStart);
          if (dwell > 0) {
            trackEvent('dwell_time', { path: from.fullPath, ms: Math.round(dwell) });
          }
        }
        lastStart = now;
        lastPath = to.fullPath || to.path;
        trackPageView(lastPath, document.title);
        setUserContext();
        resetScrollDepthState();
      });
    }

    // Global outbound link tracking
    const isExternal = (href: string) => {
      try {
        const url = new URL(href, window.location.href);
        return url.origin !== window.location.origin;
      } catch {
        return false;
      }
    };

    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest?.('a') as HTMLAnchorElement | null;
      if (!anchor || !anchor.href) return;
      if (isExternal(anchor.href)) {
        trackEvent('outbound_link_click', {
          href: anchor.href,
          target: anchor.target || '_self',
        });
      }
      // Share tracking by attribute
      const shareEl = target.closest?.('[data-ga-share]');
      if (shareEl) {
        const label = (shareEl as HTMLElement).getAttribute('data-ga-share') || 'share';
        trackEvent('share_click', { label });
      }
    };
    window.addEventListener('click', handleGlobalClick, true);

    // Client error tracking
    const handleError = (event: ErrorEvent) => {
      trackEvent('error_client', {
        message: event.message?.slice(0, 200),
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = (event.reason && (event.reason.message || String(event.reason))) || 'unknown';
      trackEvent('error_client', {
        message: String(reason).slice(0, 200),
        type: 'unhandledrejection',
      });
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Scroll depth tracking (25/50/75/100 per page)
    let scrollReported: Record<string, boolean> = {};
    const resetScrollDepthState = () => {
      scrollReported = { '25': false, '50': false, '75': false, '100': false };
    };
    resetScrollDepthState();
    const onScroll = () => {
      try {
        const doc = document.documentElement;
        const scrollTop = window.scrollY || doc.scrollTop;
        const viewport = window.innerHeight;
        const height = doc.scrollHeight - viewport;
        if (height <= 0) return;
        const percent = Math.min(100, Math.round((scrollTop / height) * 100));
        const checkpoints = [25, 50, 75, 100] as const;
        checkpoints.forEach((cp) => {
          const key = String(cp);
          if (!scrollReported[key] && percent >= cp) {
            scrollReported[key] = true;
            trackEvent('scroll_depth', { percent: cp });
          }
        });
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.debug('[GA] scroll_depth error', e);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Copy tracking (PII 주의: 내용은 미전송)
    const onCopy = () => {
      trackEvent('copy', { path: location.hash?.slice(1) || location.pathname });
    };
    window.addEventListener('copy', onCopy);

    // Generic search/filter/sort tracking
    let lastSearchEmit = 0;
    const onInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (!target) return;
      const isSearch =
        target.type === 'search' ||
        target.getAttribute('data-ga-search') === 'true' ||
        target.getAttribute('role') === 'searchbox';
      if (!isSearch) return;
      const now = Date.now();
      if (now - lastSearchEmit < 1000) return; // throttle
      lastSearchEmit = now;
      const value = target.value || '';
      trackEvent('search', { query_length: value.length });
    };
    const onChange = (e: Event) => {
      const el = e.target as HTMLElement & { value?: string };
      if (!el) return;
      if (el.hasAttribute('data-ga-filter')) {
        const name = el.getAttribute('data-ga-filter') || 'filter';
        trackEvent('filter_change', { filter_name: name, value: (el as any).value ?? '' });
      }
      if (el.hasAttribute('data-ga-sort')) {
        const name = el.getAttribute('data-ga-sort') || 'sort';
        trackEvent('sort_change', { sort_by: name, value: (el as any).value ?? '' });
      }
      if (el.tagName === 'FORM') {
        const id = (el as HTMLFormElement).id || (el as HTMLFormElement).getAttribute('name') || '';
        trackEvent('form_submit', { form: id });
      }
    };
    window.addEventListener('input', onInput, true);
    window.addEventListener('change', onChange, true);
    window.addEventListener('submit', onChange as any, true);

    // Reactive context updates
    try {
      const project = projectStore();
      const role = roleStore();
      const profile = profileStore();
      watch(
        () => [project.selectedProjectId, role.isClient, profile.profile],
        () => setUserContext(),
        { deep: true }
      );
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[GA] watch context error', e);
    }

    // Optional: Web Vitals (only if lib is available)
    if (process.env.VUE_APP_ENABLE_WEB_VITALS === 'true') {
      const loadWebVitalsFromCDN = () =>
        new Promise<void>((resolve, reject) => {
          if (window.webVitals) {
            resolve();
            return;
          }
          const script = document.createElement('script');
          script.async = true;
          script.src = 'https://unpkg.com/web-vitals@4/dist/web-vitals.iife.js';
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load web-vitals'));
          document.head.appendChild(script);
        });

      loadWebVitalsFromCDN()
        .then(() => {
          const vitals = window.webVitals;
          if (!vitals) return;
          const send = (metric: any) => {
            trackEvent('web_vital', {
              name: metric.name,
              id: metric.id,
              value: metric.value,
              rating: metric.rating,
            });
          };
          vitals.onCLS(send);
          vitals.onFID(send);
          vitals.onLCP(send);
          if (typeof vitals.onINP === 'function') vitals.onINP(send);
          vitals.onTTFB(send);
        })
        .catch((e) => {
          if (process.env.NODE_ENV !== 'production') console.debug('[GA] load web-vitals error', e);
        });
    }
  },
};

export default AnalyticsPlugin;
export type { AnalyticsOptions };


